import { useQuery } from "@tanstack/react-query";

import { api } from "@utils/api";

import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import {
  Appointment,
  AppointmentCategory,
  AppointmentStatus,
  HomeCareDetail,
  TeleconsultationAppointmentDetail,
} from "@customTypes/appointment";
import { Pagination, SortBy } from "@customTypes/index";

import { appointmentQKeys } from "./queryKeys";


const select = (res: any) => {
  // console.log(res.data);
  const pagination: Pagination = {
    page: res.data.pagination.page,
    pageCount: res.data.pagination.page_count,
    pageSize: res.data.pagination.page_size,
    total: res.data.pagination.total,
  };

  if (res.data.data.length) {
    const newData: Appointment[] = [];

    for (const i in res.data.data) {
      const {
        _id,
        department,
        medium,
        doctor_assigned,
        status,
        category,
        details,
        is_follow_up,
        created_at,
        updated_at,
        payment_transaction_details,
        appointment_date,
        consultation_id,
        consultation_c_id,
        patient_id,
        priority,
        promo_code,
        files,
      } = res.data.data[i];
      // console.log(details);

      if (category === "teleconsultation") {
        newData.push({
          id: _id,
          medium,
          department,
          doctorAssigned: {
            id: doctor_assigned?._id,
            fullName: doctor_assigned?.full_name,
          },
          consultationId: consultation_id ?? "",
          consultationCId: consultation_c_id ?? "",
          callChannelId: consultation_c_id
            ? `consultation_${consultation_c_id}`
            : "",
          patientId: patient_id,
          status,
          category,
          createdAt: new Date(created_at),
          updatedAt: new Date(updated_at),
          isFollowUp: is_follow_up,
          appointmentDate: new Date(appointment_date),
          priority: priority ? priority : null,
          details: {
            // Will be deprecated soon
            serviceDate:
              details.t_service_date && details.t_service_date !== "string"
                ? new Date(details.t_service_date)
                : new Date(1900, 0, 1),
            dob: details.dob,
            firstName: details.first_name,
            gender: details.gender,
            middleName: details.middle,
            phoneNumber: details.phone_number,
            surname: details.surname,
            serviceType: details.t_service_type,
            serviceTypeId: details.t_service_type_id,
            city: details.t_city,
            paymentMethod: details.t_payment_method,
            cost: details.t_cost ?? 0,
            callStatus: details.t_call_status ?? "not_started",
            callTime: details.t_call_time ?? 0,
            callLogs: details.call_logs?.map((log: any) => ({
              startTime: log.start_time ? new Date(log.start_time) : undefined,
              endTime: log.end_time ? new Date(log.end_time) : null,
              duration: log.duration ?? null,
            })),
            additionalHealthInfo: details.t_additional_health_info,
            //* No longer used
            hasInsurance:
              typeof details.has_insurance === "boolean"
                ? details.has_insurance
                : details.has_insurance === "Non"
                ? false
                : true,
            insuranceType: details.insurance_type,
            insuranceInfo: details.insurance_info,
          } as TeleconsultationAppointmentDetail,
          paymentTransaction: {
            amountPaid: payment_transaction_details?.amount_paid ?? 0,
            date: new Date(payment_transaction_details?.date),
          },
          promoCode: promo_code || "",
        });
      } else if (category === "homecare") {
        newData.push({
          id: _id,
          medium,
          department,
          doctorAssigned: {
            id: doctor_assigned?._id,
            fullName: doctor_assigned?.full_name,
          },
          consultationId: consultation_id ?? "",
          patientId: patient_id,
          status,
          category,
          createdAt: new Date(created_at),
          updatedAt: new Date(updated_at),
          appointmentDate: new Date(appointment_date),
          priority,
          details: {
            city: details.city,
            zone: details.zone,
            neighborhood: details.neighborhood,
            // Will be deprecated soon
            serviceDate:
              details.service_date && details.service_date !== "string"
                ? new Date(details.service_date)
                : new Date(1970, 0, 1, 0, 0),
            serviceType: details.service_type,
            serviceTypeId: details.service_type_id,
            additionalHealthInfo: details.additional_health_info,
            //* No longer used
            hasInsurance:
              typeof details.has_insurance === "boolean"
                ? details.has_insurance
                : details.has_insurance === "Non"
                ? false
                : true,
            insuranceInfo: details.insurance_info,
            insuranceType: details.insurance_type,
            paymentMethod: details.payment_method,
            cost: details.cost ?? 0,
            exigences: details.exigences,
            dob: details.dob,
            firstName: details.first_name,
            gender: details.gender,
            middleName: details.middle,
            phoneNumber: details.phone_number,
            surname: details.surname,
          } as HomeCareDetail,
          paymentTransaction: {
            amountPaid: payment_transaction_details?.amount_paid ?? 0,
            date: new Date(payment_transaction_details?.date),
          },
          promoCode: promo_code || "",
        });
      } else if (category === "lab") {
        newData.push({
          id: _id,
          medium,
          department,
          doctorAssigned: {
            id: doctor_assigned?._id,
            fullName: doctor_assigned?.full_name,
          },
          consultationId: consultation_id ?? "",
          patientId: patient_id,
          status,
          category,
          createdAt: new Date(created_at),
          updatedAt: new Date(updated_at),
          isFollowUp: is_follow_up,
          appointmentDate: new Date(appointment_date),
          priority: priority ? priority : null,
          details: {
            // Will be deprecated soon
            serviceDate:
              details.l_service_date && details.l_service_date !== "string"
                ? new Date(details.l_service_date)
                : new Date(1900, 0, 1),
            dob: details.dob,
            firstName: details.first_name,
            gender: details.gender,
            middleName: details.middle,
            phoneNumber: details.phone_number,
            surname: details.surname,
            serviceType: details.l_service_type,
            serviceTypeId: details.l_service_type_id, // todo: Remove this later, it might not be needed - Check with backend
            serviceTypeIds: details?.l_service_type_ids?.map((item: any) => ({
              label: item.type,
              value: item._id,
            })),
            city: details.l_city,
            paymentMethod: details.l_payment_method,
            cost: details.l_cost ?? 0,
            additionalHealthInfo: details.l_additional_health_info,
            //* No longer used
            hasInsurance:
              typeof details.has_insurance === "boolean"
                ? details.has_insurance
                : details.has_insurance === "Non"
                ? false
                : true,
            insuranceType: details.insurance_type,
            insuranceInfo: details.insurance_info,
          } as TeleconsultationAppointmentDetail,
          paymentTransaction: {
            amountPaid: payment_transaction_details?.amount_paid ?? 0,
            date: new Date(payment_transaction_details?.date),
          },
          promoCode: promo_code || "",
          files,
        });
      }
    }

    // console.log(newData);
    return {
      newData,
      pagination,
    };
  }
  return null;
};

interface QueryProps {
  category: AppointmentCategory;
  status: AppointmentStatus;
  pagination?: Partial<Pagination>;
  search?: string;
  isEnabled?: boolean;
  sortBy?: SortBy;
  filterBy?: string; // Doctor id
  isQueued?: boolean;
}

export const useGetAppointments = ({
  isEnabled = true,
  category,
  status,
  pagination,
  search = "",
  sortBy = "desc",
  filterBy = "",
  isQueued,
}: QueryProps) => {
  const handleErrors = useQueryErrorHandling();

  return useQuery({
    queryKey: appointmentQKeys.allWithFilters({
      category,
      status,
      ...(search && { search }),
      ...(filterBy && { filterBy }),
      ...(isQueued && { isQueued }),
      sortBy,
      pagination,
    }),
    queryFn: () => {
      return api.get("appointments/", {
        params: {
          category,
          status,
          order_by: sortBy,
          queued: isQueued,
          ...(filterBy && { doctor_assigned: filterBy }),
          ...(search && { search }),
          ...(pagination && {
            page: pagination.page,
            page_size: pagination.pageSize,
          }),
        },
      });
    },
    select,
    enabled: isEnabled,
    staleTime: Infinity,

    onError: (res: any) => {
      console.error("[From Get Incoming Appointments]", res);
      handleErrors(res, "query");
    },
  });
};
